import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Routes, Route, useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { Loading, StoreDateRangeFilter } from '../components/utility/Utility.jsx';
import {
  storeApiBoundsFromMatch,
  formatMatchRangeForDisplay,
  storeMatchRangeLastNCalendarDays,
  storeMatchRangeThisWeek,
  storeMatchRangeLastWeek,
  storeMatchRangeThisMonth,
  storeMatchRangeLastMonth,
  storeMatchRangeThisYear,
  storeMatchRangeLastYear
} from '../utils/storeDateRange';
import {
  MultiSelectFilter,
  IdNameMultiSelectFilter,
  SingleSelectDropdown
} from './ProductAnalysis';
import '../assets/css/components/program.css';
import '../assets/css/product-briefs.css';
import {
  InsightsBanner,
  BriefStatusFilter,
  BriefSortFilter,
  BriefProductTypeFilter,
  BriefCard,
  BriefMockupHero,
  briefMockupReady,
  BriefPendingCard,
  BriefListEmpty,
  SelectedFilterChips,
  BriefGeneratePreview,
  BriefSourceProductsPanel,
  formatBriefDate,
  badgeClass,
  badgeLabel,
  confidencePercent,
  ConfidenceBar,
  PaletteSwatches,
  CopyDirectionBlock,
  ReferenceProductsList,
  DataRationaleBlock,
  CompositionBlock,
  ConfidenceSignalsChips,
  FeedbackWidget,
  StatusControl
} from '../components/ProductBriefsComponents';

const BRIEF_GENERATE_DEFAULT_RANGE = 'Last 90 Days';
const BRIEF_PRODUCT_SELECTION_MAX = 20;
const BRIEF_PREVIEW_TOP_N = 20;

axios.defaults.withCredentials = true;

const API = 'https://api.amplifyloyalty.com';

const BRIEF_JOB_POLL_MS = 2000;
const BRIEF_JOB_POLL_MAX = 90;

/**
 * Poll GET /app/product-briefs/jobs/{jobId} until completed, error, or timeout.
 * @returns {() => void} cancel
 */
function pollBriefJob(req, jobId, callbacks) {
  let cancelled = false;
  let timer;
  let n = 0;

  const tick = async () => {
    if (cancelled) return;
    n += 1;
    if (n > BRIEF_JOB_POLL_MAX) {
      callbacks.onTimeout?.();
      return;
    }
    try {
      const res = await req({ method: 'GET', url: `${API}/app/product-briefs/jobs/${jobId}` });
      const j = res.data;
      if (cancelled) return;
      callbacks.onUpdate?.(j);
      if (j.status === 'completed') {
        callbacks.onComplete?.(j);
        return;
      }
      if (j.status === 'error') {
        callbacks.onError?.(j.error || 'Generation failed.');
        return;
      }
    } catch (e) {
      if (!cancelled) {
        const status = e.response?.status;
        if (status === 404) callbacks.onError?.('Job not found.');
        else callbacks.onError?.(e.response?.data?.message || e.message || 'Poll error');
      }
      return;
    }
    timer = setTimeout(tick, BRIEF_JOB_POLL_MS);
  };

  tick();
  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

function useAuthedAxios() {
  const { getJwtToken } = useContext(AuthContext);
  return useCallback(
    async (config) => {
      const token = await getJwtToken();
      return axios({
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${token}` }
      });
    },
    [getJwtToken]
  );
}

function BriefsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || 'all';
  const sort = searchParams.get('sort') || 'newest';
  const productType = searchParams.get('product_type') || '';
  const req = useAuthedAxios();

  const [insights, setInsights] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [pollJobId, setPollJobId] = useState('');
  const [pendingPhase, setPendingPhase] = useState('idle');
  const [pendingBrief, setPendingBrief] = useState(null);
  const [pendingError, setPendingError] = useState('');
  const [pendingJobStatus, setPendingJobStatus] = useState('');

  useEffect(() => {
    const id = searchParams.get('pending_job');
    if (id) setPollJobId(id);
  }, [searchParams]);

  const clearPendingJob = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('pending_job');
    setSearchParams(next, { replace: true });
    setPollJobId('');
    setPendingPhase('idle');
    setPendingBrief(null);
    setPendingError('');
    setPendingJobStatus('');
  }, [searchParams, setSearchParams]);

  const setStatus = (s) => {
    const next = new URLSearchParams(searchParams);
    if (s === 'all') next.delete('status');
    else next.set('status', s);
    setSearchParams(next);
  };
  const setSort = (s) => {
    const next = new URLSearchParams(searchParams);
    next.set('sort', s);
    setSearchParams(next);
  };
  const setProductType = (t) => {
    const next = new URLSearchParams(searchParams);
    if (!t) next.delete('product_type');
    else next.set('product_type', t);
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const q = new URLSearchParams();
      q.set('limit', '50');
      q.set('skip', '0');
      q.set('sort', sort);
      if (status !== 'all') q.set('status', status);
      if (productType) q.set('product_type', productType);
      const [insRes, listRes] = await Promise.all([
        req({ method: 'GET', url: `${API}/app/product-briefs/insights` }).catch(() => ({ data: null })),
        req({ method: 'GET', url: `${API}/app/product-briefs?${q.toString()}` })
      ]);
      if (insRes?.data?.success) setInsights(insRes.data.insights);
      else setInsights(null);
      if (!listRes.data?.success) throw new Error(listRes.data?.message || 'Failed to load briefs');
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || 'Error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [req, sort, status, productType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!pollJobId) return undefined;
    setPendingPhase('polling');
    setPendingBrief(null);
    setPendingError('');
    setPendingJobStatus('queued');

    const cancel = pollBriefJob(req, pollJobId, {
      onUpdate: (j) => setPendingJobStatus(j.status || 'running'),
      onComplete: async (j) => {
        const bid = j.brief_id;
        if (!bid) {
          setPendingPhase('error');
          setPendingError('Generation completed but no brief was created.');
          load();
          return;
        }
        try {
          const res = await req({ method: 'GET', url: `${API}/app/product-briefs/${bid}` });
          if (!res.data?.success || !res.data?.brief) {
            throw new Error(res.data?.message || 'Failed to load new brief');
          }
          setPendingBrief(res.data.brief);
          setPendingPhase('ready');
          setPollJobId('');
          const next = new URLSearchParams(searchParams);
          next.delete('pending_job');
          setSearchParams(next, { replace: true });
          load();
        } catch (e) {
          setPendingPhase('error');
          setPendingError(e.response?.data?.message || e.message || 'Failed to load new brief');
        }
      },
      onError: (message) => {
        setPendingPhase('error');
        setPendingError(message);
      },
      onTimeout: () => {
        setPendingPhase('error');
        setPendingError('Timed out waiting for generation (3 minutes).');
      }
    });

    return cancel;
  }, [pollJobId, req, load, searchParams, setSearchParams]);

  const productTypes = useMemo(() => {
    const s = new Set();
    (items || []).forEach((b) => {
      if (b.product_type) s.add(b.product_type);
    });
    if (productType && !s.has(productType)) s.add(productType);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [items, productType]);

  const lastGeneratedLabel = useMemo(() => {
    if (insights?.generated_at) return formatBriefDate(insights.generated_at);
    const first = items[0];
    if (first?.generated_at) return formatBriefDate(first.generated_at);
    return '';
  }, [insights, items]);

  const onStatusChange = async (brief, newStatus) => {
    setStatusBusyId(brief._id);
    try {
      await req({
        method: 'PATCH',
        url: `${API}/app/product-briefs/status`,
        data: { brief_id: brief._id, status: newStatus }
      });
      await load();
      if (pendingBrief?._id === brief._id) {
        setPendingBrief((p) => (p ? { ...p, status: newStatus } : p));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setStatusBusyId(null);
    }
  };

  const pendingPolling = pendingPhase === 'polling';
  const showPendingSlot =
    pendingPhase === 'polling' || pendingPhase === 'error' || (pendingPhase === 'ready' && pendingBrief);
  const listItems = useMemo(() => {
    const excludeId = pendingBrief?._id;
    if (!excludeId) return items;
    return items.filter((b) => String(b._id) !== String(excludeId));
  }, [items, pendingBrief?._id]);
  const showGrid = showPendingSlot || listItems.length > 0;

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters float-element product-briefs-filters">
          <div className="product-briefs-filters__intro-wrap">
            <div className="product-briefs-filters__intro">
              <div className="dashboard__title">Product Briefs</div>
              {lastGeneratedLabel && (
                <div className="product-briefs-filters__subtitle">Last run: {lastGeneratedLabel}</div>
              )}
            </div>
            <button
              type="button"
              className="product-briefs__btn product-briefs__btn--primary"
              disabled={pendingPolling}
              onClick={() => navigate('/app/product-analysis/briefs/new')}
            >
              Generate new brief
            </button>
          </div>
          <div className="product-briefs-filters__controls">
            <BriefStatusFilter status={status} setStatus={setStatus} />
            <BriefProductTypeFilter types={productTypes} productType={productType} setProductType={setProductType} />
            <BriefSortFilter sort={sort} setSort={setSort} />
          </div>
        </div>  

        {err && (
          <div className="float-element product-briefs__inline-msg product-briefs__error" role="alert">
            {err}
          </div>
        )}

        {loading && !showPendingSlot ? (
          <div className="float-element product-briefs__loading-row">
            <Loading />
          </div>
        ) : !showGrid ? (
          <div className="dashboard__row product-briefs__main-row">
            <BriefListEmpty onGenerate={() => navigate('/app/product-analysis/briefs/new')} />
          </div>
        ) : (
          <>
            <div className="dashboard__row product-briefs__main-row">
              <div className="product-briefs__grid">
                {showPendingSlot &&
                  (pendingPhase === 'ready' && pendingBrief ? (
                    <BriefCard
                      key={`pending-ready-${pendingBrief._id}`}
                      brief={pendingBrief}
                      onStatusChange={onStatusChange}
                      statusBusyId={statusBusyId}
                    />
                  ) : (
                    <BriefPendingCard
                      key={`pending-${pollJobId || 'job'}`}
                      status={pendingJobStatus}
                      error={pendingPhase === 'error' ? pendingError : ''}
                      onDismiss={pendingPhase === 'error' ? clearPendingJob : undefined}
                    />
                  ))}
                {listItems.map((b) => (
                  <BriefCard key={b._id} brief={b} onStatusChange={onStatusChange} statusBusyId={statusBusyId} />
                ))}
              </div>
            </div>
            <p className="product-briefs__page-footer">
              Showing {listItems.length + (pendingBrief ? 1 : 0)} of{' '}
              {total + (pendingBrief && !items.some((b) => String(b._id) === String(pendingBrief._id)) ? 1 : 0)}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function BriefsGeneratePage() {
  const navigate = useNavigate();
  const { getJwtToken, userProfile } = useContext(AuthContext);
  const timezone = userProfile?.store?.settings?.timezone;
  const req = useAuthedAxios();
  const [filterMode, setFilterMode] = useState('sales');
  const [activeRange, setActiveRange] = useState(BRIEF_GENERATE_DEFAULT_RANGE);
  const [match, setMatch] = useState(null);
  const [facets, setFacets] = useState({
    collections: [],
    product_types: []
  });
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [filters, setFilters] = useState({
    product_ids: [],
    collection_ids: [],
    product_types: []
  });
  const [desiredConfidence, setDesiredConfidence] = useState(70);
  const [outputProductType, setOutputProductType] = useState('');
  const [catalogProductTypes, setCatalogProductTypes] = useState([]);
  const [catalogTypesLoading, setCatalogTypesLoading] = useState(false);
  const [facetsLoading, setFacetsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const matchKey = useMemo(
    () => `${match?.start ? match.start.getTime() : ''}-${match?.end ? match.end.getTime() : ''}`,
    [match]
  );

  const rangeDisplayLabel = useMemo(
    () => formatMatchRangeForDisplay(timezone, activeRange, match),
    [timezone, activeRange, match]
  );

  useEffect(() => {
    if (!timezone || match) return;
    setMatch(storeMatchRangeLastNCalendarDays(timezone, 90));
  }, [timezone, match]);

  useEffect(() => {
    if (filterMode !== 'sales' || !timezone || !match?.start || !match?.end) return;
    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    let cancelled = false;
    setFacetsLoading(true);
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/app/product-analysis/filter-candidates`,
          { start: matchStart, end: matchEnd, timezone },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const data = res.data || {};
        const incomingCollections = Array.isArray(data.collections) ? data.collections : [];
        const collectionsIdName = incomingCollections
          .map((c) => ({ id: Number(c?.id), name: String(c?.title ?? c?.name ?? '') }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0);
        setFacets({
          collections: collectionsIdName,
          product_types: data.product_types || []
        });
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setFacetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterMode, timezone, matchKey, getJwtToken]);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/app/product-analysis/filter-candidates`,
          { titles_scope: 'all', titles_only: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const data = res.data || {};
        const incomingTitles = Array.isArray(data.titles) ? data.titles : [];
        const titlesIdName = incomingTitles
          .map((t) => ({ id: Number(t?.id), name: String(t?.title ?? t?.name ?? '') }))
          .filter((t) => Number.isFinite(t.id) && t.id > 0);
        setCatalogProducts(titlesIdName);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getJwtToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/app/product-analysis/filter-candidates`,
          { collections_scope: 'all', collections_only: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const incoming = Array.isArray(res.data?.collections) ? res.data.collections : [];
        const collectionsIdName = incoming
          .map((c) => ({ id: Number(c?.id), name: String(c?.title ?? c?.name ?? '') }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0);
        setFacets((prev) => ({ ...prev, collections: collectionsIdName }));
      } catch (e) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getJwtToken]);

  useEffect(() => {
    let cancelled = false;
    setCatalogTypesLoading(true);
    (async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `${API}/app/product-analysis/filter-candidates`,
          { product_types_scope: 'all', product_types_only: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        const types = Array.isArray(res.data?.product_types) ? res.data.product_types : [];
        setCatalogProductTypes(types);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setCatalogTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getJwtToken]);

  const outputProductTypeOptions = useMemo(
    () => [
      { id: '', label: 'Any' },
      ...catalogProductTypes.map((t) => ({ id: t, label: t }))
    ],
    [catalogProductTypes]
  );

  const titleById = useMemo(() => {
    const m = new Map();
    for (const t of catalogProducts || []) {
      m.set(String(t.id), t.name || String(t.id));
    }
    return m;
  }, [catalogProducts]);

  const selectedProductCount = (filters.product_ids || []).length;

  const previewRequestKey = useMemo(() => {
    if (filterMode === 'sales') {
      return JSON.stringify({
        mode: 'sales',
        matchKey,
        types: filters.product_types || [],
        collection_ids: (filters.collection_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0)
      });
    }
    return JSON.stringify({
      mode: 'products',
      ids: (filters.product_ids || []).map(String).sort()
    });
  }, [filterMode, matchKey, filters.product_types, filters.collection_ids, filters.product_ids]);

  useEffect(() => {
    if (!timezone) return;
    if (filterMode === 'products' && selectedProductCount < 1) {
      setPreview(null);
      setPreviewError('');
      setPreviewLoading(false);
      return;
    }
    if (filterMode === 'sales' && (!match?.start || !match?.end)) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const productIds = (filters.product_ids || [])
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n) && n > 0);
        const body = {
          filters: {
            ...(filterMode === 'sales'
              ? {
                  product_types: filters.product_types || [],
                  collection_ids: (filters.collection_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0)
                }
              : { product_ids: productIds })
          }
        };
        if (filterMode === 'sales') {
          const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(
            timezone,
            match.start,
            match.end
          );
          body.date_range = { start: matchStart, end: matchEnd };
        }
        const res = await req({
          method: 'POST',
          url: `${API}/app/product-briefs/generate-preview`,
          data: body
        });
        if (!cancelled) {
          setPreview(res.data?.success ? res.data : null);
          if (!res.data?.success) {
            setPreviewError(res.data?.message || 'Preview unavailable.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(e.response?.data?.message || e.message || 'Preview failed.');
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [timezone, previewRequestKey, filterMode, match, filters, selectedProductCount, req]);

  const startGenerationDisabled =
    busy ||
    previewLoading ||
    (filterMode === 'sales' && (facetsLoading || (preview && !preview.qualifies))) ||
    (filterMode === 'products' &&
      (catalogLoading || selectedProductCount < 1 || (preview && !preview.qualifies)));

  const selectedChipItems = useMemo(() => {
    const items = [];
    if (filterMode === 'sales') {
      for (const t of filters.product_types || []) {
        items.push({ id: t, label: t, group: 'Type' });
      }
      for (const id of filters.collection_ids || []) {
        const c = (facets.collections || []).find((x) => String(x.id) === String(id));
        items.push({ id, label: c?.name || String(id), group: 'Collection' });
      }
    } else {
      for (const id of filters.product_ids || []) {
        items.push({ id, label: titleById.get(String(id)) || String(id), group: 'Product' });
      }
    }
    return items;
  }, [filters, titleById, filterMode, facets.collections]);

  const switchFilterMode = (mode) => {
    if (mode === filterMode) return;
    setFilterMode(mode);
    if (mode === 'sales') {
      setFilters((p) => ({ ...p, product_ids: [] }));
    } else {
      setFilters((p) => ({
        ...p,
        product_types: [],
        collection_ids: [],
        product_ids: (p.product_ids || []).slice(0, BRIEF_PRODUCT_SELECTION_MAX)
      }));
    }
  };

  const handleChipRemove = (id, group) => {
    if (group === 'Type') {
      setFilters((p) => ({
        ...p,
        product_types: (p.product_types || []).filter((x) => x !== id)
      }));
    } else if (group === 'Collection') {
      setFilters((p) => ({
        ...p,
        collection_ids: (p.collection_ids || []).filter((x) => String(x) !== String(id))
      }));
    } else if (group === 'Product') {
      setFilters((p) => ({
        ...p,
        product_ids: (p.product_ids || []).filter((x) => String(x) !== String(id))
      }));
    }
  };

  const novelPct = 100 - desiredConfidence;

  const submit = async (e) => {
    e.preventDefault();
    if (!timezone) {
      setMsg('Store timezone is required.');
      return;
    }
    if (filterMode === 'sales' && (!match?.start || !match?.end)) {
      setMsg('Select a sales date range.');
      return;
    }
    const productIds = (filters.product_ids || [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (filterMode === 'products' && productIds.length < 1) {
      setMsg('Select at least one product.');
      return;
    }
    if (filterMode === 'products' && productIds.length > BRIEF_PRODUCT_SELECTION_MAX) {
      setMsg(`Select at most ${BRIEF_PRODUCT_SELECTION_MAX} products.`);
      return;
    }
    setMsg('');
    setBusy(true);
    try {
      const body = {
        filters: {
          desired_confidence: desiredConfidence,
          ...(outputProductType ? { output_product_type: outputProductType } : {}),
          ...(filterMode === 'sales'
            ? {
                product_types: filters.product_types || [],
                collection_ids: (filters.collection_ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0)
              }
            : { product_ids: productIds })
        }
      };
      if (filterMode === 'sales') {
        const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
        body.date_range = { start: matchStart, end: matchEnd };
      }
      const res = await req({
        method: 'POST',
        url: `${API}/app/product-briefs/generate-constrained`,
        data: body
      });
      if (res.status === 202 && res.data?.job_id) {
        setBusy(false);
        navigate(`/app/product-analysis/briefs?pending_job=${encodeURIComponent(res.data.job_id)}`);
      } else {
        setMsg(res.data?.message || 'Unexpected response');
        setBusy(false);
      }
    } catch (e) {
      setMsg(e.response?.data?.message || e.message || 'Request failed');
      setBusy(false);
    }
  };

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters float-element product-briefs-filters product-briefs-filters--compact">
          <div className="product-briefs-filters__intro-wrap">
            <div className="product-briefs-filters__intro">
              <div className="dashboard__title">Generate product brief</div>
              <div className="product-briefs-filters__subtitle">
                Optional filters; generation runs on the server and may take a minute.
              </div>
            </div>
            <Link to="/app/product-analysis/briefs" className="product-briefs__btn product-briefs__btn--secondary">
              Back to list
            </Link>
          </div>
        </div>
        {msg && (
          <div className="float-element product-briefs__inline-msg product-briefs__error" role="alert">
            {msg}
          </div>
        )}
        <form className="float-element product-briefs-generate-settings" onSubmit={submit}>
          <div className="product-briefs-generate-settings__split">
            <section className="product-briefs-generate-settings__column product-briefs-generate-settings__column--analysis">
              <h2 className="product-briefs-generate-settings__title">Analysis settings</h2>
              <p className="product-briefs__muted product-briefs-generate-settings__column-intro">
                Choose whether to narrow by sales activity or by specific products. Leave sales filters empty to use
                all qualifying products in the range.
              </p>
            <div
              className="product-briefs-generate-mode"
              role="radiogroup"
              aria-label="How to narrow the analysis"
            >
              <button
                type="button"
                role="radio"
                aria-checked={filterMode === 'sales'}
                className={`product-briefs-generate-mode__btn${filterMode === 'sales' ? ' product-briefs-generate-mode__btn--active' : ''}`}
                disabled={busy}
                onClick={() => switchFilterMode('sales')}
              >
                Filter by sales
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={filterMode === 'products'}
                className={`product-briefs-generate-mode__btn${filterMode === 'products' ? ' product-briefs-generate-mode__btn--active' : ''}`}
                disabled={busy}
                onClick={() => switchFilterMode('products')}
              >
                Filter by product
              </button>
            </div>
            <div className="product-briefs-generate-settings__filters-col">
          {filterMode === 'sales' ? (
            <>
              <div className="product-briefs-generate-settings__range-row">
                <div className="date-filter promotions-table-filters__filter-wrapper">
                  <StoreDateRangeFilter
                    active={activeRange}
                    setActive={setActiveRange}
                    timezone={timezone}
                    match={match}
                    disabled={busy}
                    popoverAlign="start"
                    options={[
                  {
                    text: 'Last 7 Days',
                    action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 7))
                  },
                  {
                    text: 'Last 14 Days',
                    action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 14))
                  },
                  {
                    text: 'Last 30 Days',
                    action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 30))
                  },
                  {
                    text: 'Last 90 Days',
                    action: () => setMatch(storeMatchRangeLastNCalendarDays(timezone, 90))
                  },
                  {
                    text: 'This Week',
                    action: () => setMatch(storeMatchRangeThisWeek(timezone))
                  },
                  {
                    text: 'Last Week',
                    action: () => setMatch(storeMatchRangeLastWeek(timezone))
                  },
                  {
                    text: 'This Month',
                    action: () => setMatch(storeMatchRangeThisMonth(timezone))
                  },
                  {
                    text: 'Last Month',
                    action: () => setMatch(storeMatchRangeLastMonth(timezone))
                  },
                  {
                    text: 'This Year',
                    action: () => setMatch(storeMatchRangeThisYear(timezone))
                  },
                  {
                    text: 'Last Year',
                    action: () => setMatch(storeMatchRangeLastYear(timezone))
                  },
                  {
                    text: 'Custom Range',
                    action: (start, end) => setMatch({ start, end })
                  }
                    ]}
                  />
                </div>
              </div>
              <div className="product-briefs-generate-settings__filters product-analysis-filters__cluster-row product-analysis-filters__cluster-row--controls">
                <MultiSelectFilter
                  label="Product type"
                  options={facets.product_types}
                  value={filters.product_types}
                  onChange={(product_types) => setFilters((p) => ({ ...p, product_types }))}
                  showSearch
                  sortAlphabetically
                />
                <IdNameMultiSelectFilter
                  label="Collection"
                  options={facets.collections}
                  value={filters.collection_ids}
                  onChange={(collection_ids) => setFilters((p) => ({ ...p, collection_ids }))}
                  showSearch
                  emptyText={facetsLoading ? 'Loading collections…' : 'No collections in catalog'}
                />
              </div>
            </>
          ) : (
            <div className="product-briefs-generate-settings__products-panel">
              <div className="product-briefs-generate-settings__filters product-analysis-filters__cluster-row product-analysis-filters__cluster-row--controls">
                <IdNameMultiSelectFilter
                  label="Product"
                  options={catalogProducts}
                  value={filters.product_ids}
                  onChange={(product_ids) => setFilters((p) => ({ ...p, product_ids }))}
                  showSearch
                  maxSelection={BRIEF_PRODUCT_SELECTION_MAX}
                  emptyText={catalogLoading ? 'Loading products…' : 'No products in catalog'}
                />
              </div>
              <p className="product-briefs__muted product-briefs-generate-settings__products-hint">
                Select up to {BRIEF_PRODUCT_SELECTION_MAX} products from your full catalog. Generation uses only the
                selected product(s).
              </p>
            </div>
          )}
            </div>
            </section>
            <section className="product-briefs-generate-settings__column product-briefs-generate-settings__column--output">
              <h2 className="product-briefs-generate-settings__title">Output settings</h2>
              <p className="product-briefs__muted product-briefs-generate-settings__column-intro">
                Controls what the generated brief recommends.
              </p>
              <div className="product-briefs-generate-settings__output-fields">
                <div className="product-briefs-generate-creative">
                  <div className="product-briefs-generate-creative__header">
                    <span className="product-briefs-generate-creative__label">Creative mix</span>
                    <span className="product-briefs-generate-creative__value">
                      {desiredConfidence}% proven · {novelPct}% new
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={desiredConfidence}
                    disabled={busy}
                    className="product-briefs-generate-creative__range"
                    aria-label="Creative mix — percent proven patterns"
                    onChange={(e) => setDesiredConfidence(Number(e.target.value))}
                  />
                  <div className="product-briefs-generate-creative__ends">
                    <span>More new ideas</span>
                    <span>More proven patterns</span>
                  </div>
                </div>
                <div>
                  <SingleSelectDropdown
                    label="Product type"
                    options={outputProductTypeOptions}
                    value={outputProductType}
                    onChange={setOutputProductType}
                    disabled={busy || catalogTypesLoading}
                    ariaLabel="Brief output product type"
                  />
                </div>
              </div>
            </section>
          </div>
          {selectedChipItems.length > 0 && (
            <SelectedFilterChips items={selectedChipItems} onRemove={handleChipRemove} />
          )}
          <BriefGeneratePreview
            preview={preview}
            loading={previewLoading}
            error={previewError}
            mode={filterMode}
            topN={BRIEF_PREVIEW_TOP_N}
          />
          <div className="product-briefs-generate-settings__actions">
            <button
              type="submit"
              className="product-briefs__btn product-briefs__btn--primary"
              disabled={startGenerationDisabled}
            >
              {busy ? 'Running…' : 'Start generation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BriefsDetailPage() {
  const { briefId } = useParams();
  const req = useAuthedAxios();
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fbBusy, setFbBusy] = useState(false);
  const [stBusy, setStBusy] = useState(false);
  const [mockupRetryBusy, setMockupRetryBusy] = useState(false);
  const [mockupRetryErr, setMockupRetryErr] = useState('');

  const load = useCallback(
    async (opts = {}) => {
      if (!briefId) return;
      if (!opts.silent) {
        setLoading(true);
        setErr('');
      }
      try {
        const res = await req({ method: 'GET', url: `${API}/app/product-briefs/${briefId}` });
        if (!res.data?.success) throw new Error(res.data?.message || 'Not found');
        setBrief(res.data.brief);
        if (!opts.silent) setErr('');
      } catch (e) {
        if (!opts.silent) {
          setErr(e.response?.data?.message || e.message || 'Error');
          setBrief(null);
        }
      } finally {
        if (!opts.silent) setLoading(false);
      }
    },
    [briefId, req]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (brief?.mockup_image?.status !== 'pending') return undefined;
    const timer = setInterval(() => load({ silent: true }), 2500);
    return () => clearInterval(timer);
  }, [brief?.mockup_image?.status, load]);

  const onRetryMockup = async () => {
    if (!brief) return;
    setMockupRetryBusy(true);
    setMockupRetryErr('');
    try {
      const res = await req({
        method: 'POST',
        url: `${API}/app/product-briefs/mockup-retry`,
        data: { brief_id: brief._id }
      });
      if (!res.data?.success) throw new Error(res.data?.message || 'Retry failed');
      setBrief(res.data.brief);
    } catch (e) {
      setMockupRetryErr(e.response?.data?.message || e.message || 'Could not start mockup retry');
    } finally {
      setMockupRetryBusy(false);
    }
  };

  const onFeedback = async (payload) => {
    if (!brief) return;
    setFbBusy(true);
    try {
      await req({
        method: 'PATCH',
        url: `${API}/app/product-briefs/feedback`,
        data: { brief_id: brief._id, ...payload }
      });
      await load();
    } finally {
      setFbBusy(false);
    }
  };

  const onStatus = async (newStatus) => {
    if (!brief) return;
    setStBusy(true);
    try {
      await req({
        method: 'PATCH',
        url: `${API}/app/product-briefs/status`,
        data: { brief_id: brief._id, status: newStatus }
      });
      await load();
    } finally {
      setStBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="product-analysis-scroll">
        <div className="dashboard dashboard--product-analysis">
          <div className="float-element product-briefs__loading-row">
            <Loading />
          </div>
        </div>
      </div>
    );
  }
  if (err || !brief) {
    return (
      <div className="product-analysis-scroll">
        <div className="dashboard dashboard--product-analysis">
          <div className="float-element product-briefs__inline-msg">
            <p className="product-briefs__error">{err || 'Brief not found.'}</p>
            <Link to="/app/product-analysis/briefs" className="product-briefs__btn product-briefs__btn--secondary">
              Back to list
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters float-element product-briefs-filters product-briefs-filters--detail">
          <Link to="/app/product-analysis/briefs" className="product-briefs-filters__back">
            ← Product briefs
          </Link>

          <div className="product-brief-detail-header">
            {(briefMockupReady(brief) ||
              ['pending', 'error', 'skipped'].includes(brief.mockup_image?.status)) && (
              <div className="product-brief-detail-header__mockup">
                <BriefMockupHero
                  brief={brief}
                  variant="detail-inline"
                  onRetryMockup={onRetryMockup}
                  mockupRetryBusy={mockupRetryBusy}
                />
              </div>
            )}
            <div className="product-brief-detail-header__content">
              <div className="product-briefs-filters__detail-head">
                <div className="product-briefs-filters__detail-text">
                  <div className="dashboard__title product-briefs-filters__detail-title">{brief.title}</div>
                  <div className="product-briefs-filters__subtitle">
                    {brief.product_type || '—'} · Generated {formatBriefDate(brief.generated_at)}
                  </div>
                </div>
                <div className="product-briefs-filters__detail-badges">
                  <span className={badgeClass(brief.badge_type)}>{badgeLabel(brief.badge_type)}</span>
                  <span className="product-brief-badge product-brief-badge--confidence" title="Model confidence">
                    {confidencePercent(brief.confidence)}% Confidence
                  </span>
                </div>
              </div>
          <div className="product-briefs-filters__detail-toolbar product-briefs-filters__detail-toolbar--header">
            <div className="product-briefs-filters__detail-swatches">
              <PaletteSwatches colorPalette={brief.color_palette} />
            </div>
            <div className="product-briefs-filters__detail-status">
              <label className="product-briefs-filters__status-label" htmlFor={`brief-status-${brief._id}`}>
                Status
              </label>
              <StatusControl
                    brief={brief}
                    onStatusChange={(_, s) => onStatus(s)}
                    disabled={stBusy}
                    id={`brief-status-${brief._id}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {mockupRetryErr && <p className="product-briefs__error product-brief-mockup__retry-err">{mockupRetryErr}</p>}
        </div>

        <div className="product-briefs-detail-body">
          {(brief.source_products?.length > 0 || brief.generation_basis) && (
            <div className="float-element product-briefs-detail-panel">
              <BriefSourceProductsPanel brief={brief} />
            </div>
          )}
          <div className="float-element product-briefs-detail-panel">
            <ConfidenceSignalsChips signals={brief.confidence_signals} />
            <div className="product-brief-detail__section product-brief-detail__section--tight-top">
              <h3>Attributes</h3>
              {(brief.subjects?.length ||
                (brief.themes || brief.mood)?.length ||
                (brief.design_styles || brief.design_style)) && (
                <div className="product-brief-detail__tags-inline">
                  {[...(brief.subjects || [])].map((x) => (
                    <span key={`s-${x}`} className="product-brief-tag">
                      {x}
                    </span>
                  ))}
                  {(
                    Array.isArray(brief.design_styles)
                      ? brief.design_styles
                      : brief.design_style
                        ? [brief.design_style]
                        : []
                  ).map((x) => (
                    <span key={`d-${x}`} className="product-brief-tag">
                      {x}
                    </span>
                  ))}
                  {[...(Array.isArray(brief.themes) ? brief.themes : brief.mood || [])].map((x) => (
                    <span key={`t-${x}`} className="product-brief-tag">
                      {x}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="float-element product-briefs-detail-panel">
            <CompositionBlock composition={brief.composition} printLocation={brief.print_location} />
          </div>

          {(brief.copy_suggestions ||
            (brief.reference_products && brief.reference_products.length > 0) ||
            brief.what_to_avoid) && (
            <div className="float-element product-briefs-detail-panel">
              <CopyDirectionBlock copy={brief.copy_suggestions} />
              <ReferenceProductsList items={brief.reference_products} />
              {brief.what_to_avoid && (
                <div className="product-brief-detail__section">
                  <h3>What to avoid</h3>
                  <p className="product-briefs__muted">{brief.what_to_avoid}</p>
                </div>
              )}
            </div>
          )}

          {brief.data_rationale && (
            <div className="float-element product-briefs-detail-panel">
              <DataRationaleBlock text={brief.data_rationale} />
            </div>
          )}

          <div className="float-element product-briefs-detail-panel">
            <FeedbackWidget brief={brief} onSubmit={onFeedback} busy={fbBusy} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProductBriefs() {
  return (
    <Routes>
      <Route index element={<BriefsListPage />} />
      <Route path="new" element={<BriefsGeneratePage />} />
      <Route path=":briefId" element={<BriefsDetailPage />} />
    </Routes>
  );
}
